/* Copyright (c) 2026. Amazon.com, Inc. or its affiliates. All rights reserved. */

import { createPersistentItem, globalRef, memoize } from '@amazon/vinyl-util'
import { boolean } from '@amazon/vinyl-validation'
import { data, type ObservableValue } from '@amazon/vinyl-observable'
import { asError } from '@/util/errorUtil'
import { queryParam } from '@/util/queryParams'
import { ctx } from '@/util/context'

const { errorHandler } = ctx('featureGate')

/**
 * A feature flag backed by a persisted localStorage override. Kept a distinct
 * type from {@link FeatureFlag} so {@link setPersistentItemFeatureGate} can only
 * be called with a flag that actually has a persisted backing store.
 */
export type PersistentItemFeatureFlag =
    | 'agenticChat'
    | 'amp'
    | 'asinHlsMse'
    | 'livestreamAtcPreselect'
    | 'merch'
    | 'videoPodcasts'
    | 'visualizer'

/**
 * Every feature flag. A superset of {@link PersistentItemFeatureFlag}: a flag
 * may later resolve from web lab or the feature-gate service rather than a
 * persisted item. The two sets are exactly equal today.
 */
export type FeatureFlag = PersistentItemFeatureFlag

/** Every known feature flag, so callers (e.g. the debug menu) can enumerate them. */
export const FEATURE_FLAGS: readonly FeatureFlag[] = [
    'agenticChat',
    'amp',
    'asinHlsMse',
    'livestreamAtcPreselect',
    'merch',
    'videoPodcasts',
    'visualizer',
]

/**
 * Flags whose value may be overridden for the current page load by a
 * `?feature.<flag>=true|false` query param. An allow-list, so a URL can't flip
 * an arbitrary gate — only flags explicitly opted in here.
 */
const QUERY_OVERRIDABLE_FLAGS: readonly FeatureFlag[] = [
    'agenticChat',
    'amp',
    'asinHlsMse',
    'livestreamAtcPreselect',
    'merch',
    'videoPodcasts',
    'visualizer',
]

const FEATURE_FLAG_DEFAULTS: Record<FeatureFlag, boolean> = {
    agenticChat: false,
    amp: false,
    asinHlsMse: true,
    livestreamAtcPreselect: false,
    merch: true,
    videoPodcasts: true,
    visualizer: false,
}

/**
 * The resolution state of a feature gate. A gate can resolve asynchronously (a
 * persisted item today; web lab or the feature-gate service later), so a plain
 * boolean can't represent the pre-resolution window — a consumer that renders
 * one way or the other must wait out `loading` (see {@link FeatureGateEnabled}).
 */
export type FeatureGateResult =
    | { result: boolean; error: null; loading: false }
    | { result: null; error: Error; loading: false }
    | { result: null; error: null; loading: true }

/** The localStorage key backing a persisted feature-gate override, e.g. `featureGate.amp`. */
export function featureGateStorageKey(flag: PersistentItemFeatureFlag): string {
    return `featureGate.${flag}`
}

/**
 * Every persisted feature-gate override key. A gate override is a device/dev
 * preference, not per-account data — the sign-out teardown preserves these
 * across its `localStorage.clear()` the same way it preserves `deviceInfo`.
 */
export const FEATURE_GATE_STORAGE_KEYS: readonly string[] = FEATURE_FLAGS.map(featureGateStorageKey)

const persistentItemGate = memoize(
    (flag: PersistentItemFeatureFlag) =>
        createPersistentItem<boolean>({
            key: featureGateStorageKey(flag),
            defaultValue: FEATURE_FLAG_DEFAULTS[flag],
            // A stored non-boolean (legacy junk, manual tampering) is rejected and
            // falls back to the flag's default.
            validator: boolean().assert,
        }),
    (flag) => flag
)

/**
 * Persist a gate override for a persisted-item-backed flag. Read back by
 * {@link featureEnabled} on the next page load; the debug menu pairs this with a
 * reload prompt.
 */
export function setPersistentItemFeatureGate(
    flag: PersistentItemFeatureFlag,
    enabled: boolean
): Promise<void> {
    return persistentItemGate(flag).set(enabled)
}

// `?feature.<flag>=true|false`. A dot needs no query-string escaping, so the
// param reads cleanly. Allow-listed flags only; any other value is ignored.
function readQueryOverride(flag: FeatureFlag): boolean | undefined {
    const raw = queryParam(`feature.${flag}`)
    if (raw === 'true') return true
    if (raw === 'false') return false
    return undefined
}

// Read at module load: an override belongs to the URL the page loaded with, and a
// gate first asked after an in-app navigation would otherwise miss it.
const QUERY_OVERRIDES: ReadonlyMap<FeatureFlag, boolean | undefined> = new Map(
    QUERY_OVERRIDABLE_FLAGS.map((flag) => [flag, readQueryOverride(flag)])
)

// The single resolution per flag: a query override wins, else the persisted item
// (later: web lab / the feature-gate service). Memoized so a flag resolves ONCE
// per page load and every consumer shares that one promise. Not runtime dynamic:
// a persisted change takes effect on the next load.
const resolveGateRef = globalRef(() =>
    memoize(
        (flag: FeatureFlag): Promise<boolean> => {
            const override = QUERY_OVERRIDES.get(flag)
            if (override !== undefined) return Promise.resolve(override)
            return persistentItemGate(flag).get()
        },
        (flag) => flag
    )
)

function resolveGate(flag: FeatureFlag): Promise<boolean> {
    return resolveGateRef.value(flag)
}

/**
 * Whether a feature is enabled. Async so the value can come from a persisted
 * override (and, later, web lab / the feature-gate service) without blocking
 * boot. On a backing-source error it reports to the error handler and falls back
 * to the flag's shipped default — the raw error is still exposed via
 * {@link featureEnabled$} for consumers that render an error state.
 */
export async function featureEnabled(flag: FeatureFlag): Promise<boolean> {
    try {
        return await resolveGate(flag)
    } catch (error: unknown) {
        errorHandler(asError(error))
        return FEATURE_FLAG_DEFAULTS[flag]
    }
}

const featureGateResultRef = globalRef(() =>
    memoize(
        (flag: FeatureFlag): ObservableValue<FeatureGateResult> => {
            const result$ = data<FeatureGateResult>({ result: null, error: null, loading: true })
            resolveGate(flag).then(
                (result) => (result$.value = { result, error: null, loading: false }),
                (error: unknown) =>
                    (result$.value = { result: null, error: asError(error), loading: false })
            )
            return result$
        },
        (flag) => flag
    )
)

/**
 * Reactive resolution state of a feature flag as a {@link FeatureGateResult}, so
 * a consumer can distinguish loading / error / result rather than assume a
 * boolean is ready. Prefer the {@link FeatureGateEnabled} / {@link
 * FeatureGateDisabled} wrappers for component-level gating; imperative callers
 * await {@link featureEnabled}.
 */
export function featureEnabled$(flag: FeatureFlag): ObservableValue<FeatureGateResult> {
    return featureGateResultRef.value(flag)
}
